import { Injectable } from '@angular/core';
import { sp } from '@pnp/sp';
import { ListTitles } from './constants';
import { AercKeyContact, Asset, AssetNormalized, Channel, Indication, IndicationNormalized, TA } from './models';

@Injectable({
  providedIn: 'root'
})
export class GeneralService {

  showChannelNavigator = false;

  appliedFiltersSummary = "";

  filteredChannels: Channel[] = [];

  selectedTAIds: number[] = [];
  selectedAssetIds: { Title: string, Ids: number[] }[] = [];
  selectedIndicationIds: { Title: string, Ids: number[] }[] = [];

  appliedTAIds: number[] = [];
  appliedAssetIds: { Title: string, Ids: number[] }[] = [];
  appliedIndicationIds: { Title: string, Ids: number[] }[] = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() { }

  isTaChecked(id: number): boolean {
    return this.selectedTAIds.findIndex(x => x == id) != -1;
  }

  isAssetChecked(title: string): boolean {
    return this.selectedAssetIds.findIndex(x => x.Title === title) != -1;
  }

  isIndicationChecked(title: string): boolean {
    return this.selectedIndicationIds.findIndex(x => x.Title === title) != -1;
  }

  selectedByTas(id: number): void {
    const index = this.selectedTAIds.findIndex(x => x == id);
    if (index != -1) {
      this.selectedTAIds = this.selectedTAIds.filter(x => x != id);
    }
    else {
      this.selectedTAIds.push(id);
    }
  }

  selectedByAssets(items: Asset[]): void {
    const index = this.selectedAssetIds.findIndex(x => items[0].Title === x.Title);
    if (index != -1) {
      this.selectedAssetIds.splice(index, 1);
    }
    else {
      this.selectedAssetIds.push({ Title: items[0].Title, Ids: items.map(x => x.Id) });
    }
  }

  selectedByIndications(items: Indication[]): void {
    const index = this.selectedIndicationIds.findIndex(x => items[0].Title === x.Title);
    if (index != -1) {
      this.selectedIndicationIds.splice(index, 1);
    }
    else {
      this.selectedIndicationIds.push({ Title: items[0].Title, Ids: items.map(x => x.Id) });
    }
  }

  clearAll(): void {
    this.selectedTAIds = [];
    this.selectedAssetIds = [];
    this.selectedIndicationIds = [];
    this.appliedFiltersSummary = "";
  }

  async applyChannelFilters(channels: Channel[]): Promise<Channel[]> {
    const queryFilter = this.buildSPFilter();
    const resultChannels = channels.filter(x => x.ListName && x.ListName.length > 0 && x.ListName != 'Glossary Terms');

    const listsItemsPromises = resultChannels.map(x => {
      if (x.ListName == 'ContractRightBestPracticeExamples') {
        if (queryFilter) {
          const split = queryFilter.split(" ");
          let result1 = (split[0].substring(0, split[0].length - 2));
          let result2 = split[1] + " " + split[2];
          let crquerystring = result1 + 's' + " " + result2
          return sp.web.lists.getByTitle(x.ListName).items.filter(crquerystring).top(1).get()
        }
        else {
          return sp.web.lists.getByTitle(x.ListName).items.filter(queryFilter).top(1).get()
        }
      }
      else {
        return sp.web.lists.getByTitle(x.ListName).items.filter(queryFilter).top(1).get()
      }
    }
    );

    const listsItems = await Promise.all(listsItemsPromises);

    return resultChannels.filter((x, i) => listsItems[i].length > 0);
  }

  buildSPFilter(): string {
    let query = '';

    if (this.selectedTAIds.length > 0) {
      this.selectedTAIds = this.selectedTAIds.filter((x) => x);
      query += this.selectedTAIds.map(x => `TAId eq ${x}`).join(' or ');
    }

    if (this.selectedAssetIds.length > 0) {
      if (query.length > 0) query += ' and ';
      this.selectedAssetIds = this.selectedAssetIds.filter((x) => x);
      const assetIds = this.selectedAssetIds.map(x => x.Ids).reduce((a, b) => a.concat(b));

      query += assetIds.map(x => `AssetId eq ${x}`).join(' or ');
    }

    if (this.selectedIndicationIds.length > 0) {
      if (query.length > 0) query += ' and ';
      this.selectedIndicationIds = this.selectedIndicationIds.filter((x) => x);
      const indicationIds = this.selectedIndicationIds.map(x => x.Ids).reduce((a, b) => a.concat(b));

      query += indicationIds.map(x => `IndicationId eq ${x}`).join(' or ');
    }

    return query;
  }

  buildAppliedFiltersSummary(TAs: TA[], assets: AssetNormalized[], indications: IndicationNormalized[]): void {
    this.appliedFiltersSummary = "";
    if (this.selectedTAIds.length > 0) {

      this.appliedTAIds = Object.assign([], this.selectedTAIds);

      this.appliedFiltersSummary += this.getTasFilterText(TAs);
      if (this.tasFilterCountResume > 0)
        this.appliedFiltersSummary += `+${this.tasFilterCountResume}`;
    }
    if (this.selectedAssetIds.length > 0) {
      this.appliedAssetIds = Object.assign([], this.selectedAssetIds);

      if (this.appliedFiltersSummary.length > 0) {
        this.appliedFiltersSummary += '-';
      }
      this.appliedFiltersSummary += this.getAssetsFilterText(assets);
      if (this.assetsFilterCountResume > 0)
        this.appliedFiltersSummary += `+${this.assetsFilterCountResume}`;
    }
    if (this.selectedIndicationIds.length > 0) {
      this.appliedIndicationIds = Object.assign([], this.selectedIndicationIds);

      if (this.appliedFiltersSummary.length > 0) {
        this.appliedFiltersSummary += '-';
      }
      this.appliedFiltersSummary += this.getIndicationsFilterText(indications);
      if (this.indicationsFilterCountResume > 0)
        this.appliedFiltersSummary += `+${this.indicationsFilterCountResume}`;
    }
  }

  get tasFilterCountResume(): number { return this.selectedTAIds.length - 1; }

  getTasFilterText(TAs: TA[]): string {
    const filter = TAs.filter(x => this.selectedTAIds.includes(x.Id));
    return filter.length > 0 ? filter[0].Title : '';
  }

  get assetsFilterCountResume(): number { return this.selectedAssetIds.length - 1; }

  getAssetsFilterText(assets: AssetNormalized[]): string {
    const filter = assets.filter(x => this.selectedAssetIds.some(y => y.Title === x.Title))
    return filter.length > 0 ? filter[0].Title : '';
  }

  get indicationsFilterCountResume(): number { return this.selectedIndicationIds.length - 1; }

  getIndicationsFilterText(indications: IndicationNormalized[]): string {
    const filter = indications.filter(x => this.selectedIndicationIds.some(y => y.Title === x.Title));
    return filter.length > 0 ? filter[0].Title : '';
  }

  getPastDecisionActivationKey(): Promise<string> {
    return sp.web.lists.getByTitle('GeneralConfigurations').items.filter("Title eq 'Prismaccess Registration Code'").select('Value').getAll().then(x => {
      const activationKey = x[0];
      if (activationKey && activationKey !== undefined)
        return activationKey['Value'];
      else
        return '';
    })
  }

  getAercKeyContact(): Promise<AercKeyContact> {
    return sp.web.lists.getByTitle('GeneralConfigurations').items.filter("Title eq 'AercKeyContact'").select('Value').getAll().then(x => {
      const activationKey = x[0];
      if (activationKey && activationKey !== undefined && activationKey['Value'])
        return JSON.parse(activationKey['Value']);
      else
        return '';
    })
  }

  async getFAQList(): Promise<any> {

    return await sp.web.lists.getByTitle(ListTitles.FAQSQuestions).items
      .select('*, TA/Title, Asset/Title, Indication/Title, Country/Title, Attachments, AttachmentFiles')
      .expand('TA, Asset, Indication, Country, AttachmentFiles')
      .getAll();

  }


  saveDataInSession(key: string, data: any) {
    if (key && data) {
      sessionStorage.setItem(key, JSON.stringify(data))
    }

  }
  getDataFromSession(key: string) {
    const data = sessionStorage.getItem(key);
    if (data) {
      return JSON.parse(data)
    }
    return undefined;
  }

}
